import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  CreditCard,
  Calendar as CalendarIcon,
  User,
} from 'lucide-react';
import { BilingualLabel } from './BilingualLabel';
import type { LabelKey } from '../config/labels';

interface NavItem {
  path: string;
  labelKey: LabelKey;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

interface ModernMobileNavProps {
  userAvatar?: string;
  userFullName?: string;
}

export function ModernMobileNav({ userAvatar, userFullName }: ModernMobileNavProps) {
  const location = useLocation();

  // Navigation items for modern mobile layout
  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      labelKey: 'nav.dashboard',
      icon: Home,
    },
    {
      path: '/programs',
      labelKey: 'nav.programs',
      icon: CalendarIcon,
    },
    {
      path: '/students',
      labelKey: 'nav.students',
      icon: Users,
    },
    {
      path: '/payments',
      labelKey: 'nav.payments',
      icon: CreditCard,
    },
    {
      path: '/account',
      labelKey: 'nav.account',
      icon: User,
    },
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 pb-safe">
      <div className="flex items-center justify-around px-2 py-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const isAccountItem = item.path === '/account';
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-lg transition-all duration-200 ${
                active
                  ? 'text-green-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {isAccountItem && userAvatar ? (
                <img
                  src={userAvatar}
                  alt={userFullName || 'User'}
                  className={`w-6 h-6 mb-1 rounded-full ${
                    active ? 'ring-2 ring-green-600' : ''
                  }`}
                />
              ) : (
                <Icon 
                  className={`w-6 h-6 mb-1 ${
                    active ? 'text-green-600' : 'text-gray-500'
                  }`} 
                />
              )}
              <BilingualLabel
                keyName={item.labelKey}
                className={`text-xs font-medium ${
                  active ? 'text-green-600' : 'text-gray-500'
                }`}
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
} 